import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { SorologiaViral } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import { calcularCamposAlterados } from "../../../shared/utils/trilhaAuditoriaDiff";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

interface SorologiasContextProps {
  sorologias: SorologiaViral[];
  loading: boolean;
  adicionarSorologia: (
    pacienteId: string,
    dados: Omit<SorologiaViral, "id" | "registradoPor" | "registradoPorNome">
  ) => Promise<void>;
  listarSorologiasPorPaciente: (pacienteId: string) => SorologiaViral[];
  editarSorologia: (
    id: string,
    dados: Partial<SorologiaViral>
  ) => Promise<void>;
  removerSorologia: (id: string) => Promise<void>;
  carregarSorologias: () => Promise<void>;
}

const SorologiasContext = createContext<SorologiasContextProps | undefined>(
  undefined
);

export function SorologiasProvider({ children }: { children: ReactNode }) {
  console.log("🔥 SorologiasProvider montado!"); // ← ADICIONA
  const { usuarioLogado } = useAuth();
  const [sorologias, setSorologias] = useState<SorologiaViral[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // 📥 CARREGAR SOROLOGIAS DO FIREBASE
  // ============================================
  const carregarSorologias = async () => {
    try {
      setLoading(true);
      console.log("🔍 Carregando sorologias do Firebase...");

      const querySnapshot = await getDocs(collection(db, "sorologias"));

      const sorologiasFormatadas: SorologiaViral[] = querySnapshot.docs.map(
        (docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            pacienteId: data.pacienteId,
            dataColeta:
              data.dataColeta?.toDate?.()?.toISOString?.() || data.dataColeta,

            // Hepatite B
            hbsag: data.hbsag, // ✅ CORRETO
            antiHBs: data.antiHBs, // ✅ CORRETO
            antiHBcIgG: data.antiHBcIgG, // ✅ CORRETO
            antiHBcIgM: data.antiHBcIgM,
            hbeag: data.hbeag,
            antiHBe: data.antiHBe,
            genotipagemVHB: data.genotipagemVHB,
            pcrVHB: data.pcrVHB,

            // Hepatite C
            antiHCV: data.antiHCV,
            hcvRNA: data.hcvRNA,

            // Hepatite A
            antiHAVIgG: data.antiHAVIgG,
            antiHAVIgM: data.antiHAVIgM,

            // Outras
            antiHDV: data.antiHDV,
            pcrVHD: data.pcrVHD,
            antiHEV: data.antiHEV,

            // CMV
            cmvIgG: data.cmvIgG,
            cmvIgM: data.cmvIgM,
            cmvPCR: data.cmvPCR,

            // EBV
            ebvIgG: data.ebvIgG,
            ebvIgM: data.ebvIgM,
            ebvPCR: data.ebvPCR,

            // Rubéola
            rubeolaIgG: data.rubeolaIgG,
            rubeolaIgM: data.rubeolaIgM,

            // Outros
            antiHIV: data.antiHIV,
            vdrl: data.vdrl,
            ftaAbs: data.ftaAbs,
            toxoIgG: data.toxoIgG,
            toxoIgM: data.toxoIgM,
            chagas: data.chagas,
            chagasIgG: data.chagasIgG,
            chagasIgM: data.chagasIgM,
            htlv: data.htlv,

            // Rastreio pré-Tx — parasitas / TB (AI_118)
            estrongiloidesIgG: data.estrongiloidesIgG,
            igraTB: data.igraTB,
            ppd: data.ppd,

            // Auditoria
            registradoPor: data.registradoPor,
            registradoPorNome: data.registradoPorNome,
            editadoPor: data.editadoPor,
            editadoPorNome: data.editadoPorNome,
            dataEdicao:
              data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
          } as SorologiaViral;
        }
      );

      setSorologias(sorologiasFormatadas);
      console.log("✅ Sorologias carregadas:", sorologiasFormatadas.length);
    } catch (error) {
      console.error("❌ Erro ao carregar sorologias:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 CARREGAR AO MONTAR
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("🔍 Escutando sorologias em tempo real...");

    const unsubscribe = onSnapshot(
      collection(db, "sorologias"),
      (snapshot) => {
        const lista: SorologiaViral[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
          } as SorologiaViral;
        });

        setSorologias(lista);
        setLoading(false);
        console.log("✅ Sorologias atualizadas (tempo real):", lista.length);
      },
      (error) => {
        console.error("❌ Erro no listener de sorologias:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // ➕ ADICIONAR SOROLOGIA
  // ============================================
  const adicionarSorologia = async (
    pacienteId: string,
    dados: Omit<SorologiaViral, "id" | "registradoPor" | "registradoPorNome">
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Adicionando sorologia...");

      // Remove campos undefined
      const dadosLimpos = Object.fromEntries(
        Object.entries(dados).filter(([_, value]) => value !== undefined)
      );

      await addDoc(collection(db, "sorologias"), {
        pacienteId,
        registradoPor: usuarioLogado.id,
        registradoPorNome: usuarioLogado.nome,
        ...dadosLimpos,
        criadoEm: serverTimestamp(),
      });

      console.log("✅ Sorologia adicionada com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao adicionar sorologia:", error);
      throw error;
    }
  };

  // ============================================
  // 📋 LISTAR SOROLOGIAS DE UM PACIENTE
  // ============================================
  const listarSorologiasPorPaciente = (
    pacienteId: string
  ): SorologiaViral[] => {
    return sorologias
      .filter((s) => s.pacienteId === pacienteId)
      .sort(
        (a, b) =>
          new Date(b.dataColeta).getTime() - new Date(a.dataColeta).getTime()
      );
  };

  // ============================================
  // ✏️ EDITAR SOROLOGIA
  // ============================================
  const editarSorologia = async (
    id: string,
    dados: Partial<SorologiaViral>
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Editando sorologia:", id);

      const dadosFirebase: any = {
        editadoPor: usuarioLogado.id,
        editadoPorNome: usuarioLogado.nome,
        dataEdicao: serverTimestamp(),
      };

      // Adiciona apenas campos que foram alterados.
      // FIX (AI_99, achado do teste B213): as telas mandam o objeto
      // INTEIRO de volta, incluindo `criadoEm` convertido p/ string na
      // leitura (no banco é Timestamp) — a regra de imutabilidade do B81
      // via "valor diferente" e NEGAVA o update inteiro (a edição
      // revertia). Campos imutáveis/metadados ficam fora do payload.
      const CAMPOS_BLOQUEADOS = new Set([
        "id",
        "registradoPor",
        "registradoPorNome",
        "criadoEm",
        "dataEdicao",
        "editadoPor",
        "editadoPorNome",
        "pacienteId",
      ]);
      Object.keys(dados).forEach((key) => {
        const value = (dados as any)[key];
        if (value !== undefined && !CAMPOS_BLOQUEADOS.has(key)) {
          dadosFirebase[key] = value;
        }
      });

      await updateDoc(doc(db, "sorologias", id), dadosFirebase);

      // B213 — trilha de auditoria
      const atual = sorologias.find((s) => s.id === id);
      await registrarEdicaoTrilha({
        colecao: "sorologias",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual?.dataColeta
          ? `Coleta de ${new Date(atual.dataColeta).toLocaleDateString("pt-BR")}`
          : "Sorologias",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: calcularCamposAlterados(dados, atual),
      });

      console.log("✅ Sorologia editada com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao editar sorologia:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ REMOVER SOROLOGIA
  // ============================================
  const removerSorologia = async (id: string) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("🗑️ Removendo sorologia:", id);

      // B213 — captura antes do delete (a lápide sobrevive ao doc)
      const atual = sorologias.find((s) => s.id === id);

      await deleteDoc(doc(db, "sorologias", id));

      await registrarExclusaoTrilha({
        colecao: "sorologias",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual?.dataColeta
          ? `Coleta de ${new Date(atual.dataColeta).toLocaleDateString("pt-BR")}`
          : "Sorologias",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
      });

      console.log("✅ Sorologia removida com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao remover sorologia:", error);
      throw error;
    }
  };

  return (
    <SorologiasContext.Provider
      value={{
        sorologias,
        loading,
        adicionarSorologia,
        listarSorologiasPorPaciente,
        editarSorologia,
        removerSorologia,
        carregarSorologias,
      }}
    >
      {children}
    </SorologiasContext.Provider>
  );
}

export function useSorologias() {
  const context = useContext(SorologiasContext);
  if (!context) {
    throw new Error(
      "useSorologias deve ser usado dentro de SorologiasProvider"
    );
  }
  return context;
}
